// components/finance/ImportStatementDialog.tsx

import { useRef, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Stack,
  Box,
  Divider,
  Alert,
  LinearProgress,
} from "@mui/material";
import Iconify from "../../components/iconify";

const FORMAT_OPTIONS = [
  {
    value: "csv",
    label: "CSV",
    description: "Standard comma-separated — HSBC, Barclays, most banks",
  },
  {
    value: "ofx",
    label: "OFX / QFX",
    description: "Open Financial Exchange — NatWest, Lloyds, Santander",
  },
  {
    value: "qif",
    label: "QIF",
    description: "Quicken Interchange Format — older bank exports",
  },
];

const VALIDATION_RULES = [
  "Date column must be present (DD/MM/YYYY or YYYY-MM-DD)",
  "Description column must be present",
  "Amount column must be present (negative = debit, positive = credit)",
  "Duplicate transactions are detected and skipped automatically",
  "Balance summary rows (Opening/Closing Balance) are skipped",
];

type UploadStage =
  | "idle"
  | "selected"
  | "validating"
  | "importing"
  | "done"
  | "error";

interface ValidationResult {
  valid: boolean;
  totalRows: number;
  errors: string[];
  warnings: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onImport: (file: File, format: string) => Promise<void>;
}

export default function ImportStatementDialog({
  open,
  onClose,
  onImport,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [format, setFormat] = useState("csv");
  const [file, setFile] = useState<File | null>(null);
  const [stage, setStage] = useState<UploadStage>("idle");
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [importResult, setImportResult] = useState<{
    imported: number;
    skipped: number;
    errors: number;
  } | null>(null);

  // ✅ FIX 1: Reset ALL local state every time the dialog opens
  // This ensures a file from a previous session is never re-shown
  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open]);

  const resetState = () => {
    setFile(null);
    setStage("idle");
    setProgress(0);
    setErrorMessage("");
    setImportResult(null);
    // Also clear the hidden file input so the same file can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    setFile(selected);
    setStage("selected");
    setErrorMessage("");
    setImportResult(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files[0];
    if (!dropped) return;
    setFile(dropped);
    setStage("selected");
    setErrorMessage("");
    setImportResult(null);
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    resetState();
  };

  const handleImport = async () => {
    if (!file) return;
    try {
      setStage("importing");
      setProgress(0);

      // Animate progress bar while waiting for the server
      const interval = setInterval(() => {
        setProgress((p) => {
          if (p >= 85) {
            clearInterval(interval);
            return 85;
          }
          return p + 12;
        });
      }, 250);

      await onImport(file, format);

      clearInterval(interval);
      setProgress(100);
      setStage("done");

      // ✅ FIX 2: Clear the file reference after successful import
      // so if the user opens the dialog again it starts fresh
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err: any) {
      setProgress(0);
      setErrorMessage(
        err?.response?.data?.message ??
          err?.message ??
          "Import failed. Check file format and try again.",
      );
      setStage("error");
    }
  };

  // ✅ FIX 3: handleClose always resets state
  const handleClose = () => {
    resetState();
    onClose();
  };

  const isLoading = stage === "importing";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import Bank Statement</DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Stack spacing={2.5}>
          {/* Format selector */}
          <TextField
            select
            fullWidth
            size="small"
            label="File format"
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            disabled={isLoading}
            SelectProps={{
              renderValue: (value) =>
                FORMAT_OPTIONS.find((opt) => opt.value === value)?.label ??
                (value as string),
            }}>
            {FORMAT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Stack sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {opt.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    title={opt.description}
                    sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {opt.description}
                  </Typography>
                </Stack>
              </MenuItem>
            ))}
          </TextField>

          {/* Drop zone */}
          <Box
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => !isLoading && !file && fileInputRef.current?.click()}
            sx={{
              border: "2px dashed",
              borderColor: file ? "primary.main" : "grey.300",
              borderRadius: 1.5,
              p: 3,
              textAlign: "center",
              cursor: isLoading ? "default" : file ? "default" : "pointer",
              bgcolor: file ? "primary.lighter" : "grey.50",
              transition: "all 0.2s",
              "&:hover":
                !isLoading && !file
                  ? { borderColor: "primary.main", bgcolor: "primary.lighter" }
                  : {},
            }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.ofx,.qfx,.qif"
              hidden
              onChange={handleFileSelect}
            />

            {file ? (
              // ── File selected state ──
              <Stack alignItems="center" spacing={1}>
                <Iconify
                  icon="eva:file-text-fill"
                  width={36}
                  sx={{ color: "primary.main" }}
                />
                <Typography variant="subtitle2" color="primary.dark">
                  {file.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(file.size / 1024).toFixed(1)} KB
                </Typography>
                {!isLoading && (
                  <Button
                    size="small"
                    color="inherit"
                    variant="outlined"
                    startIcon={<Iconify icon="eva:close-fill" />}
                    onClick={handleRemoveFile}
                    sx={{ mt: 0.5 }}>
                    Remove file
                  </Button>
                )}
              </Stack>
            ) : (
              // ── Empty state ──
              <Stack alignItems="center" spacing={1}>
                <Iconify
                  icon="eva:cloud-upload-fill"
                  width={36}
                  sx={{ color: "text.disabled" }}
                />
                <Typography variant="body2" color="text.secondary">
                  Drag &amp; drop your statement here, or{" "}
                  <Box
                    component="span"
                    sx={{ color: "primary.main", fontWeight: 500 }}>
                    browse
                  </Box>
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  Supports CSV, OFX, QFX, QIF · Max 10 MB
                </Typography>
              </Stack>
            )}
          </Box>

          {/* Progress bar (importing only) */}
          {isLoading && (
            <Box>
              <Stack direction="row" justifyContent="space-between" mb={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Importing and classifying transactions…
                </Typography>
                <Typography variant="caption" fontFamily="monospace">
                  {progress}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ borderRadius: 1 }}
              />
            </Box>
          )}

          {/* Success result */}
          {stage === "done" && (
            <Alert severity="success" sx={{ borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Statement imported successfully!
              </Typography>
              <Typography variant="caption" display="block">
                Transactions are now being auto-classified by the rules engine.
                Refresh the table to see results.
              </Typography>
            </Alert>
          )}

          {/* Error */}
          {stage === "error" && (
            <Alert severity="error" sx={{ borderRadius: 1 }}>
              {errorMessage}
            </Alert>
          )}

          <Divider />

          {/* Template download callout */}
          <Alert
            severity="info"
            sx={{ borderRadius: 1 }}
            icon={<Iconify icon="eva:download-fill" width={18} />}
            action={
              <Button
                color="info"
                size="small"
                href="/optopro_bank_template.csv"
                download>
                Download
              </Button>
            }>
            <Typography variant="caption" fontWeight={500}>
              Use the OptoPro CSV template
            </Typography>
            <Typography
              variant="caption"
              display="block"
              color="text.secondary">
              Includes all required columns with example data pre-filled.
            </Typography>
          </Alert>

          {/* Validation rules info */}
          <Stack spacing={0.5}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}>
              Column requirements
            </Typography>
            {VALIDATION_RULES.map((rule) => (
              <Stack
                key={rule}
                direction="row"
                spacing={0.75}
                alignItems="flex-start">
                <Iconify
                  icon="eva:checkmark-circle-fill"
                  width={14}
                  sx={{ color: "success.main", mt: 0.2, flexShrink: 0 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {rule}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} color="inherit" disabled={isLoading}>
          {stage === "done" ? "Close" : "Cancel"}
        </Button>

        {stage === "done" ? (
          // After success — offer to import another file
          <Button
            variant="outlined"
            onClick={resetState}
            startIcon={<Iconify icon="eva:plus-fill" />}>
            Import another
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!file || isLoading}
            startIcon={
              isLoading ? undefined : <Iconify icon="eva:upload-fill" />
            }>
            {isLoading ? "Importing…" : "Import"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
